import Box from "@mui/material/Box";
import Skeleton from "@mui/material/Skeleton";
import Stack from "@mui/material/Stack";
import type { SxProps, Theme } from "@mui/material/styles";

import { IgChip } from "@intelligems/ig-theme/IgChip";

export interface SuggestionChip {
  key: string;
  label: string;
  onClick: () => void;
}

/**
 * The chip row directly above an Atlas rail's composer — shared by every
 * source of chips (the store's rule ideas, the context-aware suggestions), so
 * they can never drift into two looks.
 *
 * Figma "suggestion-chips" (15427:9561): the row sits flush on the dock's
 * column and ends in a 48px fade into the rail's own background, so a chip
 * running off the end reads as "scroll for more" rather than clipped.
 */
export const SuggestionChipRow = ({
  label,
  chips,
}: {
  /** The list's accessible name. */
  label: string;
  chips: SuggestionChip[];
}) => (
  <Box sx={{ minWidth: 0, position: "relative" }}>
    <Stack
      direction="row"
      role="list"
      aria-label={label}
      sx={{
        flexWrap: "nowrap",
        gap: 2,
        overflowX: "auto",
        // The scroller spans the composer's own width, so a chip that runs
        // off the end is scrolled to rather than clipped by the rail's edge.
        minWidth: 0,
        maxWidth: "100%",
        // The row scrolls rather than wrapping: the rail is narrow and a
        // wrapped row would push the composer down as chips grow.
        scrollbarWidth: "none",
        "&::-webkit-scrollbar": { display: "none" },
      }}
    >
      {chips.map((chip) => (
        <IgChip
          key={chip.key}
          role="listitem"
          label={chip.label}
          clickable
          onClick={chip.onClick}
          sx={SUGGESTION_CHIP_SX}
        />
      ))}
    </Stack>
    <Box
      aria-hidden
      sx={(theme) => ({
        background: `linear-gradient(to right, transparent, ${theme.igBackgroundColors.baseLightLow})`,
        bottom: 0,
        pointerEvents: "none",
        position: "absolute",
        right: 0,
        top: 0,
        width: 48,
      })}
    />
  </Box>
);

/** Holds the chip row's 26px footprint while Atlas generates suggestions. */
export const SuggestionChipSkeletonRow = ({ label }: { label: string }) => (
  <Stack
    aria-label={label}
    role="status"
    direction="row"
    gap={2}
    sx={{ minWidth: 0, overflow: "hidden" }}
  >
    {[112, 136, 96].map((width) => (
      <Skeleton
        key={width}
        animation="wave"
        variant="rounded"
        height={26}
        width={width}
        sx={{ borderRadius: "64px", flexShrink: 0 }}
      />
    ))}
  </Stack>
);

/**
 * Figma "Atlas / Suggestion chip" (516:3138): a 26px pill padded 4px/12px on a
 * 64px radius, Background/Interactive/Neutral/Default on a Border/Core/Disabled
 * hairline, the label Body 2 regular in Text/Core/Tertiary. Hover lightens the
 * fill to Neutral/Hover and darkens the hairline to Border/Core/Hover.
 */
const SUGGESTION_CHIP_SX: SxProps<Theme> = (theme) => ({
  backgroundColor: theme.igBackgroundColors.interactive.neutral.default,
  border: `1px solid ${theme.igBorders.core.disabled}`,
  borderRadius: "64px",
  flexShrink: 0,
  // The theme pins every small chip to 24px with `!important`, which a plain
  // height loses to; this pill is 26px in the design.
  height: "26px !important",
  maxWidth: 240,
  px: 3,
  py: 1,
  "& .MuiChip-label": {
    color: theme.palette.text.tertiary,
    fontSize: 12,
    fontWeight: 400,
    lineHeight: "18px",
    px: 0,
  },
  "&.MuiChip-clickable:hover": {
    backgroundColor: theme.igBackgroundColors.interactive.neutral.hover,
    borderColor: theme.igBorders.core.hover,
  },
});
